// 異地備份（plan/vms-web-v0.md §5.12 BAK、plan/nvr-offsite-backup-v0.md）。
//   - 目的地（BAK-01）：NAS（S3 相容 / SMB / NFS）或另一台 NVR；登記前可先測；帳密只送一次、平台託管，畫面只看得到末 4 碼
//   - 政策精靈（BAK-02）：來源 NVR 與通道 → 目的地 → 保留 / 速率 → 容量試算 → 故障域 → 送出；核准照政策版本
//   - 覆蓋率（BAK-03）：每通道已保護到哪、落後、缺口、待傳；> 120 秒 degraded
//   - 目的端視角（BAK-06）：只看額度、健康與匿名物件數，沒有影片入口
// 只有家戶 owner 看得到這頁（nav needs: owner）；每個家戶各自列。
import { DateTimeInput } from '../../components/DateTimeInput';
import { useMemo, useState } from 'react';
import { useQueries, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { useConfirm, type ConfirmOptions } from '../../components/ConfirmDialog';
import { KinioError } from '@kinio/web-sdk';
import { Dialog } from '../../components/Dialog';
import { useScope } from '../../lib/scope';
import { useDevices } from '../../lib/useDevices';
import { fullName, type VmsDevice } from '../../lib/devices';
import { formatDateTime } from '../../lib/tz';
import { coverageSummary, faultComplete, fmtBps, fmtBytes, fmtLag, gbToBytes, targetFormError } from '../../lib/backup';
import {
  backupApi,
  type ScopeHome,
  type BackupChannel,
  type BackupCheck,
  type BackupKind,
  type BackupPolicy,
  type BackupPolicyView,
  type BackupTarget,
  type BackupTargetIn,
  type BackupTestResult,
  type FaultDomain,
} from '../../lib/planeApi';
import { PasswordInput } from '../../components/PasswordInput';

type T = (k: string, o?: Record<string, unknown>) => string;

function errText(t: T, e: unknown): string {
  if (e instanceof KinioError) {
    const b = (e.body ?? {}) as Record<string, unknown>;
    const message = typeof b.message === 'string' ? b.message : '';
    return t(`backup.err_${e.code}`, { defaultValue: message || e.code, message });
  }
  return String(e);
}

const tz = () => Intl.DateTimeFormat().resolvedOptions().timeZone;

export function BackupPage() {
  const { t } = useTranslation();
  const qc = useQueryClient();
  const { visible } = useScope();
  const owned = useMemo(() => visible.filter((h) => h.role === 'owner'), [visible]);
  const tq = useQueries({
    queries: owned.map((h) => ({ queryKey: ['backup-targets', h.home_id], queryFn: () => backupApi.targets(h.endpoints.api, h.home_id), refetchInterval: 30_000 })),
  });
  const pq = useQueries({
    queries: owned.map((h) => ({ queryKey: ['backup-policies', h.home_id], queryFn: () => backupApi.policies(h.endpoints.api, h.home_id), refetchInterval: 10_000 })),
  });
  const targets = owned.flatMap((h, i) => (tq[i]?.data ?? []).map((x) => ({ x, home: h })));
  // 同一個政策兩端都是自己的家戶：只列一次，用來源那側（看得到覆蓋率）
  const policies = useMemo(() => {
    const m = new Map<string, { v: BackupPolicyView; home: ScopeHome }>();
    owned.forEach((h, i) => {
      for (const v of pq[i]?.data ?? []) {
        const cur = m.get(v.policy.policy_id);
        if (!cur || (v.my_sides.includes('source') && !cur.v.my_sides.includes('source'))) m.set(v.policy.policy_id, { v, home: h });
      }
    });
    return [...m.values()];
  }, [owned, pq]);
  const [adding, setAdding] = useState(false);
  const [wizard, setWizard] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const refresh = () => {
    void qc.invalidateQueries({ queryKey: ['backup-targets'] });
    void qc.invalidateQueries({ queryKey: ['backup-policies'] });
  };
  const loadErr = [...tq, ...pq].find((q) => q.error)?.error;

  return (
    <section className="backup-page">
      <div className="toolbar">
        <h1 className="page-title" style={{ margin: 0 }}>
          {t('backup.title')}
        </h1>
        <span className="spacer" />
        {/* 頁首只留一顆〔新增目的地〕（還沒有目的地時由空狀態那顆負責，第二輪 B2-28）；沒有目的地時政策鈕標灰並寫原因 */}
        {targets.length > 0 && (
          <button className="btn" onClick={() => setAdding(true)} disabled={owned.length === 0} data-testid="backup-add-target">
            {t('backup.add_target')}
          </button>
        )}
        {targets.length === 0 && <span className="muted small" data-testid="backup-need-target">{t('backup.need_target')}</span>}
        <button className="btn btn-primary" onClick={() => setWizard(true)} disabled={targets.length === 0} title={targets.length === 0 ? t('backup.need_target') : undefined} data-testid="backup-new-policy">
          {t('backup.new_policy')}
        </button>
      </div>
      <p className="muted small">{t('backup.help')}</p>
      {err && <p className="error">{err}</p>}
      {loadErr && <p className="error">{errText(t, loadErr)}</p>}

      <h2>{t('backup.targets')}</h2>
      {targets.length === 0 && !tq.some((q) => q.isLoading) && (
        // 空狀態一句說明＋主要動作（SPEC 備份頁的空狀態，P1-11）
        <p className="muted" data-testid="backup-empty-targets">
          {t('backup.no_targets')}{' '}
          {owned.length > 0 && (
            <button className="btn btn-sm btn-primary" onClick={() => setAdding(true)}>
              {t('backup.add_target')}
            </button>
          )}
        </p>
      )}
      <ul className="backup-list">
        {targets.map(({ x, home }) => (
          <TargetCard key={x.target_id} target={x} home={home} onError={setErr} onChanged={refresh} />
        ))}
      </ul>

      <h2>{t('backup.policies')}</h2>
      {policies.length === 0 && !pq.some((q) => q.isLoading) && (
        <p className="muted" data-testid="backup-empty-policies">
          {targets.length > 0 ? t('backup.no_policies') : t('backup.no_policies_need_target')}{' '}
          {targets.length > 0 && (
            <button className="btn btn-sm btn-primary" onClick={() => setWizard(true)}>
              {t('backup.new_policy')}
            </button>
          )}
        </p>
      )}
      <ul className="backup-list">
        {policies.map(({ v, home }) => (
          <PolicyCard key={v.policy.policy_id} view={v} home={home} targets={targets.map((x) => x.x)} onError={setErr} onChanged={refresh} />
        ))}
      </ul>

      {adding && (
        <TargetDialog
          homes={owned}
          close={() => setAdding(false)}
          done={() => {
            setAdding(false);
            refresh();
          }}
        />
      )}
      {wizard && (
        <PolicyWizard
          homes={owned}
          targets={targets}
          close={() => setWizard(false)}
          done={() => {
            setWizard(false);
            refresh();
          }}
        />
      )}
    </section>
  );
}

function statusBadge(status: string): string {
  switch (status) {
    case 'ok':
    case 'active':
      return 'badge badge-ok';
    case 'failed':
    case 'degraded':
    case 'revoked':
      return 'badge badge-err';
    default:
      return 'badge badge-warn';
  }
}

function TestResultView({ r }: { r: BackupTestResult }) {
  const { t, i18n } = useTranslation();
  return (
    <div className={'backup-test ' + (r.ok ? 'ok' : 'fail')} data-category={r.category}>
      <strong>{r.ok ? t('backup.test_ok') : t(`backup.cat_${r.category}`)}</strong> · {r.message}
      <span className="muted small"> · {formatDateTime(Date.parse(r.at) / 1000, tz(), i18n.language)}</span>
      {r.steps.length > 0 && (
        <span className="muted small">
          {' '}
          ({r.steps.map((s) => `${t('backup.step_' + s.step, { defaultValue: s.step })} ${s.ok ? '✓' : '✗'}`).join(' → ')})
        </span>
      )}
    </div>
  );
}

function TargetCard({ target: x, home, onError, onChanged }: { target: BackupTarget; home: ScopeHome; onError(e: string | null): void; onChanged(): void }) {
  const { t } = useTranslation();
  const { ask, dialog } = useConfirm();
  const [busy, setBusy] = useState(false);
  const [rotate, setRotate] = useState(false);
  const pct = Math.min(100, Math.round((x.used_bytes / Math.max(1, x.quota_bytes)) * 100));
  const api = home.endpoints.api;

  async function test() {
    setBusy(true);
    onError(null);
    try {
      await backupApi.testTarget(api, home.home_id, x.target_id);
      onChanged();
    } catch (e) {
      onError(errText(t, e));
    } finally {
      setBusy(false);
    }
  }
  async function remove() {
    if (!(await ask({ danger: true, title: t('backup.confirm_delete_target', { name: x.name }), action: t('common.action_delete') }))) return;
    setBusy(true);
    onError(null);
    try {
      await backupApi.deleteTarget(api, home.home_id, x.target_id, x.version);
      onChanged();
    } catch (e) {
      onError(errText(t, e));
    } finally {
      setBusy(false);
    }
  }
  const where =
    x.kind === 'nas_s3'
      ? `${x.endpoint} · ${x.bucket}${x.prefix ? '/' + x.prefix : ''}`
      : x.kind === 'nvr'
        ? x.nvr_ref
        : x.endpoint;
  return (
    <li className="card backup-row" data-target={x.target_id} data-status={x.status}>
      <div className="backup-main">
        <div>
          <strong>{x.name}</strong> <span className="badge">{t(`backup.kind_${x.kind}`)}</span>{' '}
          <span className={statusBadge(x.status)}>{t(`backup.tst_${x.status}`)}</span>{' '}
          {x.access_path === 'via_device' && <span className="badge">{t('backup.via_device')}</span>}
        </div>
        <div className="muted small">
          {where} · {home.display_name || home.home_id}
        </div>
        <div className="muted small">
          {t('backup.quota_used', { used: fmtBytes(x.used_bytes), quota: fmtBytes(x.quota_bytes) })}
          {Object.keys(x.credentials ?? {}).length > 0 &&
            ' · ' + t('backup.cred_hint', { write: x.credentials.write ?? '—', read: x.credentials.read ?? '—' })}
          {' · '}
          {t('backup.policies_n', { n: x.policies })}
        </div>
        <div className="ai-cap">
          <div className={'ai-cap-bar' + (pct >= 90 ? ' full' : '')} style={{ width: `${pct}%` }} />
        </div>
        {x.last_test && <TestResultView r={x.last_test} />}
      </div>
      {x.can_manage && (
        <div className="export-actions">
          <button className="btn btn-sm" onClick={() => void test()} disabled={busy} data-testid="backup-test">
            {t('backup.test')}
          </button>
          {x.kind !== 'nvr' && (
            <button className="btn btn-sm" onClick={() => setRotate(true)} disabled={busy}>
              {t('backup.rotate')}
            </button>
          )}
          <button className="btn btn-sm" onClick={() => void remove()} disabled={busy || x.policies > 0} title={x.policies > 0 ? t('backup.in_use') : ''}>
            {t('backup.delete')}
          </button>
        </div>
      )}
      {rotate && <RotateDialog target={x} home={home} close={() => setRotate(false)} done={() => (setRotate(false), onChanged())} />}
      {dialog}
    </li>
  );
}

function RotateDialog({ target, home, close, done }: { target: BackupTarget; home: ScopeHome; close(): void; done(): void }) {
  const { t } = useTranslation();
  const [c, setC] = useState({ a: '', b: '' });
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const smb = target.kind !== 'nas_s3';
  async function submit() {
    setBusy(true);
    setErr(null);
    try {
      const write = smb ? { username: c.a, password: c.b } : { access_key_id: c.a, secret_access_key: c.b };
      await backupApi.updateTarget(home.endpoints.api, home.home_id, target.target_id, { expected_version: target.version, credentials: { write } });
      done();
    } catch (e) {
      setErr(errText(t, e));
    } finally {
      setBusy(false);
    }
  }
  return (
    <Dialog open title={t('backup.rotate_title', { name: target.name })} onClose={close}>
      <div className="form">
        <p className="muted small">{t('backup.rotate_help')}</p>
        <label className="field">
          <span>{smb ? t('backup.username') : t('backup.access_key')}</span>
          <input className="input" autoComplete="off" value={c.a} onChange={(e) => setC({ ...c, a: e.target.value })} />
        </label>
        <label className="field">
          <span>{smb ? t('backup.password') : t('backup.secret_key')}</span>
          <PasswordInput autoComplete="new-password" value={c.b} onChange={(e) => setC({ ...c, b: e.target.value })} />
        </label>
        {err && <p className="error">{err}</p>}
        <div className="row">
          <button className="btn btn-primary" onClick={() => void submit()} disabled={busy || !c.a || !c.b}>
            {t('backup.rotate')}
          </button>
        </div>
      </div>
    </Dialog>
  );
}

function FaultFields({ value, onChange, prefix }: { value: FaultDomain; onChange(v: FaultDomain): void; prefix: string }) {
  const { t } = useTranslation();
  return (
    <fieldset className="field">
      <legend>{t('backup.fault_domain')}</legend>
      <div className="check-grid">
        {(['site', 'facility', 'power', 'network'] as const).map((k) => (
          <label key={k} className="field">
            <span className="small">{t('backup.fd_' + k)}</span>
            <input className="input" data-testid={`${prefix}-fd-${k}`} value={value[k] ?? ''} onChange={(e) => onChange({ ...value, [k]: e.target.value })} />
          </label>
        ))}
      </div>
      <p className="muted small">{t('backup.fd_help')}</p>
    </fieldset>
  );
}

function TargetDialog({ homes, close, done }: { homes: ScopeHome[]; close(): void; done(): void }) {
  const { t } = useTranslation();
  const { tree } = useDevices();
  const [homeId, setHomeId] = useState(homes[0]?.home_id ?? '');
  const home = homes.find((h) => h.home_id === homeId);
  const [kind, setKind] = useState<BackupKind>('nas_s3');
  const [f, setF] = useState({ name: '', endpoint: '', bucket: '', prefix: 'kinio', region: '', quotaGB: '', access: 'public' as 'public' | 'via_device', proxy: '', nvr: '', pathStyle: true });
  const [cred, setCred] = useState({ a: '', b: '', ra: '', rb: '', separateRead: false });
  const [fd, setFd] = useState<FaultDomain>({});
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const [test, setTest] = useState<BackupTestResult | null>(null);
  const homeDevices = tree.filter((d) => d.homeId === homeId && d.kind !== 'child');
  const nvrs = homeDevices.filter((d) => d.children.length > 0);
  const enterprise = home?.org_kind === 'team';
  const lanOnly = kind === 'nas_smb' || kind === 'nas_nfs';

  function body(): BackupTargetIn {
    const s3 = kind === 'nas_s3';
    const write = s3 ? { access_key_id: cred.a, secret_access_key: cred.b } : cred.a || cred.b ? { username: cred.a, password: cred.b } : undefined;
    const read = cred.separateRead ? (s3 ? { access_key_id: cred.ra, secret_access_key: cred.rb } : { username: cred.ra, password: cred.rb }) : undefined;
    return {
      kind,
      name: f.name,
      endpoint: kind === 'nvr' ? undefined : f.endpoint.trim(),
      bucket: s3 ? f.bucket.trim() : undefined,
      prefix: s3 ? f.prefix.trim() : undefined,
      region: s3 ? f.region.trim() || undefined : undefined,
      path_style: s3 ? f.pathStyle : undefined,
      access_path: lanOnly ? 'via_device' : kind === 'nvr' ? undefined : f.access,
      proxy_ref: lanOnly || f.access === 'via_device' ? f.proxy : undefined,
      nvr_ref: kind === 'nvr' ? f.nvr : undefined,
      quota_bytes: gbToBytes(f.quotaGB),
      fault_domain: fd,
      credentials: kind === 'nvr' ? undefined : { write, read },
    };
  }
  const formErr = targetFormError(body(), true);

  async function runTest() {
    if (!home) return;
    setBusy(true);
    setErr(null);
    setTest(null);
    try {
      const r = await backupApi.testAdhoc(home.endpoints.api, home.home_id, body());
      setTest(r.result);
    } catch (e) {
      setErr(errText(t, e));
    } finally {
      setBusy(false);
    }
  }
  async function submit() {
    if (!home) return;
    setBusy(true);
    setErr(null);
    try {
      const created = await backupApi.createTarget(home.endpoints.api, home.home_id, body());
      // 登記完馬上用託管的帳密測一次（結果存在目的地上）
      if (created.kind === 'nas_s3' && created.access_path === 'public') await backupApi.testTarget(home.endpoints.api, home.home_id, created.target_id).catch(() => undefined);
      done();
    } catch (e) {
      setErr(errText(t, e));
    } finally {
      setBusy(false);
    }
  }
  const s3 = kind === 'nas_s3';
  return (
    <Dialog open title={t('backup.add_target')} onClose={close} wide>
      <div className="form" data-testid="backup-target-form">
        {homes.length > 1 && (
          <label className="field">
            <span>{t('backup.home')}</span>
            <select className="input" value={homeId} onChange={(e) => setHomeId(e.target.value)}>
              {homes.map((h) => (
                <option key={h.home_id} value={h.home_id}>
                  {h.display_name || h.home_id}
                </option>
              ))}
            </select>
          </label>
        )}
        <label className="field">
          <span>{t('backup.kind')}</span>
          <select className="input" value={kind} data-testid="backup-kind" onChange={(e) => setKind(e.target.value as BackupKind)}>
            <option value="nas_s3">{t('backup.kind_nas_s3')}</option>
            <option value="nas_smb">{t('backup.kind_nas_smb')}</option>
            <option value="nas_nfs">{t('backup.kind_nas_nfs')}</option>
            {enterprise && <option value="nvr">{t('backup.kind_nvr')}</option>}
          </select>
        </label>
        <p className="muted small">{t('backup.kind_help_' + kind)}</p>
        <label className="field">
          <span>{t('backup.name')}</span>
          <input className="input" data-testid="backup-name" value={f.name} onChange={(e) => setF({ ...f, name: e.target.value })} />
        </label>
        {kind === 'nvr' ? (
          <label className="field">
            <span>{t('backup.target_nvr')}</span>
            <select className="input" value={f.nvr} onChange={(e) => setF({ ...f, nvr: e.target.value })}>
              <option value="">—</option>
              {nvrs.map((d) => (
                <option key={d.aid} value={d.aid}>
                  {fullName(d, tree)}
                </option>
              ))}
            </select>
          </label>
        ) : (
          <label className="field">
            <span>{t(s3 ? 'backup.endpoint_s3' : kind === 'nas_smb' ? 'backup.endpoint_smb' : 'backup.endpoint_nfs')}</span>
            <input
              className="input"
              data-testid="backup-endpoint"
              placeholder={s3 ? 'https://nas.example.com:9000' : kind === 'nas_smb' ? '//nas/kinio' : 'nas:/volume1/kinio'}
              value={f.endpoint}
              onChange={(e) => setF({ ...f, endpoint: e.target.value })}
            />
          </label>
        )}
        {s3 && (
          <div className="check-grid">
            <label className="field">
              <span>{t('backup.bucket')}</span>
              <input className="input" data-testid="backup-bucket" value={f.bucket} onChange={(e) => setF({ ...f, bucket: e.target.value })} />
            </label>
            <label className="field">
              <span>{t('backup.prefix')}</span>
              <input className="input" value={f.prefix} onChange={(e) => setF({ ...f, prefix: e.target.value })} />
            </label>
            <label className="field">
              <span>{t('backup.region')}</span>
              <input className="input" placeholder="us-east-1" value={f.region} onChange={(e) => setF({ ...f, region: e.target.value })} />
            </label>
            <label className="check">
              <input type="checkbox" checked={f.pathStyle} onChange={(e) => setF({ ...f, pathStyle: e.target.checked })} />
              {t('backup.path_style')}
            </label>
          </div>
        )}
        {s3 && (
          <label className="field">
            <span>{t('backup.access_path')}</span>
            <select className="input" value={f.access} onChange={(e) => setF({ ...f, access: e.target.value as 'public' | 'via_device' })}>
              <option value="public">{t('backup.ap_public')}</option>
              <option value="via_device">{t('backup.ap_via_device')}</option>
            </select>
          </label>
        )}
        {(lanOnly || (s3 && f.access === 'via_device')) && (
          <label className="field">
            <span>{t('backup.proxy_device')}</span>
            <select className="input" value={f.proxy} onChange={(e) => setF({ ...f, proxy: e.target.value })}>
              <option value="">—</option>
              {homeDevices.map((d) => (
                <option key={d.aid} value={d.aid}>
                  {fullName(d, tree)}
                </option>
              ))}
            </select>
          </label>
        )}
        <label className="field">
          <span>{t('backup.quota_gb')}</span>
          <input className="input" inputMode="decimal" data-testid="backup-quota" value={f.quotaGB} onChange={(e) => setF({ ...f, quotaGB: e.target.value })} />
          <span className="muted small">{t('backup.quota_help')}</span>
        </label>
        {kind !== 'nvr' && (
          <fieldset className="field">
            <legend>{t('backup.credentials')}</legend>
            <p className="muted small">{t('backup.cred_help')}</p>
            <div className="check-grid">
              <label className="field">
                <span className="small">{s3 ? t('backup.access_key') : t('backup.username')}</span>
                <input className="input" autoComplete="off" data-testid="backup-cred-a" value={cred.a} onChange={(e) => setCred({ ...cred, a: e.target.value })} />
              </label>
              <label className="field">
                <span className="small">{s3 ? t('backup.secret_key') : t('backup.password')}</span>
                <PasswordInput
                  className="input"
                  autoComplete="new-password"
                  data-testid="backup-cred-b"
                  value={cred.b}
                  onChange={(e) => setCred({ ...cred, b: e.target.value })}
                />
              </label>
            </div>
            <label className="check">
              <input type="checkbox" checked={cred.separateRead} onChange={(e) => setCred({ ...cred, separateRead: e.target.checked })} />
              {t('backup.separate_read')}
            </label>
            {cred.separateRead && (
              <div className="check-grid">
                <label className="field">
                  <span className="small">{t('backup.read_prefix')} {s3 ? t('backup.access_key') : t('backup.username')}</span>
                  <input className="input" autoComplete="off" value={cred.ra} onChange={(e) => setCred({ ...cred, ra: e.target.value })} />
                </label>
                <label className="field">
                  <span className="small">{t('backup.read_prefix')} {s3 ? t('backup.secret_key') : t('backup.password')}</span>
                  <PasswordInput autoComplete="new-password" value={cred.rb} onChange={(e) => setCred({ ...cred, rb: e.target.value })} />
                </label>
              </div>
            )}
          </fieldset>
        )}
        <FaultFields value={fd} onChange={setFd} prefix="target" />
        {test && <TestResultView r={test} />}
        {formErr && <p className="muted small">{t(formErr)}</p>}
        {err && <p className="error">{err}</p>}
        <div className="row">
          {kind === 'nas_s3' && (
            <button className="btn" onClick={() => void runTest()} disabled={busy || !!formErr} data-testid="backup-test-adhoc">
              {t('backup.test_first')}
            </button>
          )}
          <button className="btn btn-primary" onClick={() => void submit()} disabled={busy || !!formErr} data-testid="backup-save-target">
            {t('backup.save_target')}
          </button>
        </div>
      </div>
    </Dialog>
  );
}

function PolicyWizard({
  homes,
  targets,
  close,
  done,
}: {
  homes: ScopeHome[];
  targets: { x: BackupTarget; home: ScopeHome }[];
  close(): void;
  done(): void;
}) {
  const { t } = useTranslation();
  const { tree } = useDevices();
  const nvrs = tree.filter((d) => d.kind !== 'child' && d.children.length > 0 && homes.some((h) => h.home_id === d.homeId));
  const [nvrKey, setNvrKey] = useState(nvrs[0]?.key ?? '');
  const nvr: VmsDevice | undefined = nvrs.find((d) => d.key === nvrKey);
  const home = homes.find((h) => h.home_id === nvr?.homeId);
  const [picked, setPicked] = useState<Record<string, 'main' | 'sub'>>({});
  const avail = targets.filter(({ x, home: th }) => x.status !== 'disabled' && (th.home_id === home?.home_id || (!!home?.org_id && th.org_id === home.org_id)));
  const [targetId, setTargetId] = useState('');
  const [retention, setRetention] = useState('30');
  const [rateMbps, setRateMbps] = useState('');
  const [quotaGB, setQuotaGB] = useState('');
  const [fd, setFd] = useState<FaultDomain>({});
  const [confirmed, setConfirmed] = useState(false);
  const [check, setCheck] = useState<BackupCheck | null>(null);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const tgt = avail.find((a) => a.x.target_id === targetId)?.x;

  function body(dry: boolean) {
    const channels: BackupChannel[] = Object.entries(picked).map(([channel_ref, stream]) => ({ channel_ref, stream }));
    return {
      source_nvr_ref: nvr?.aid ?? '',
      target_id: targetId,
      channels,
      retention_days: Number(retention) || 0,
      rate_limit_bps: rateMbps ? Math.round(Number(rateMbps) * 1e6) : undefined,
      quota_bytes: gbToBytes(quotaGB) || undefined,
      source_fault_domain: fd,
      site_confirmed: confirmed,
      dry_run: dry,
    };
  }
  async function run(dry: boolean) {
    if (!home) return;
    setBusy(true);
    setErr(null);
    try {
      const r = await backupApi.createPolicy(home.endpoints.api, home.home_id, body(dry));
      setCheck(r.check);
      if (!dry && r.policy) {
        // 送出即代表來源方核准這一版（同一個人是兩端 owner 時目的方也一起）
        await backupApi.approve(home.endpoints.api, home.home_id, r.policy.policy_id, { expected_policy_version: r.policy.policy_version }).catch(() => undefined);
        done();
      }
    } catch (e) {
      setErr(errText(t, e));
      if (e instanceof KinioError && e.detail?.check) setCheck(e.detail.check as BackupCheck);
    } finally {
      setBusy(false);
    }
  }
  const ready = !!nvr && Object.keys(picked).length > 0 && !!targetId && Number(retention) > 0;
  return (
    <Dialog open title={t('backup.new_policy')} onClose={close} wide>
      <div className="form" data-testid="backup-wizard">
        <ol className="wizard-steps muted small">
          <li>{t('backup.wz1')}</li>
          <li>{t('backup.wz2')}</li>
          <li>{t('backup.wz3')}</li>
          <li>{t('backup.wz4')}</li>
          <li>{t('backup.wz5')}</li>
          <li>{t('backup.wz6')}</li>
        </ol>
        <label className="field">
          <span>{t('backup.source_nvr')}</span>
          <select
            className="input"
            value={nvrKey}
            onChange={(e) => {
              setNvrKey(e.target.value);
              setPicked({});
              setTargetId('');
            }}
          >
            {nvrs.map((d) => (
              <option key={d.key} value={d.key}>
                {fullName(d, tree)}
              </option>
            ))}
          </select>
        </label>
        {nvrs.length === 0 && <p className="notice">{t('backup.no_nvr')}</p>}
        {nvr && (
          <fieldset className="field">
            <legend>{t('backup.channels')}</legend>
            <div className="check-grid">
              {nvr.children.map((c) => (
                <label key={c.aid} className="check">
                  <input
                    type="checkbox"
                    checked={!!picked[c.aid]}
                    onChange={(e) => {
                      const p = { ...picked };
                      if (e.target.checked) p[c.aid] = 'main';
                      else delete p[c.aid];
                      setPicked(p);
                      setCheck(null);
                    }}
                  />
                  {c.name}
                  {picked[c.aid] && (
                    <select className="input input-sm" value={picked[c.aid]} onChange={(e) => setPicked({ ...picked, [c.aid]: e.target.value as 'main' | 'sub' })}>
                      <option value="main">{t('backup.stream_main')}</option>
                      <option value="sub">{t('backup.stream_sub')}</option>
                    </select>
                  )}
                </label>
              ))}
            </div>
          </fieldset>
        )}
        <label className="field">
          <span>{t('backup.target')}</span>
          <select className="input" data-testid="backup-target-select" value={targetId} onChange={(e) => (setTargetId(e.target.value), setCheck(null))}>
            <option value="">—</option>
            {avail.map(({ x, home: th }) => (
              <option key={x.target_id} value={x.target_id}>
                {x.name} · {t(`backup.kind_${x.kind}`)} · {th.display_name || th.home_id}
                {x.status !== 'ok' ? ` · ${t(`backup.tst_${x.status}`)}` : ''}
              </option>
            ))}
          </select>
          {tgt && tgt.home_id !== home?.home_id && <span className="muted small">{t('backup.needs_target_approval')}</span>}
        </label>
        <div className="check-grid">
          <label className="field">
            <span>{t('backup.retention_days')}</span>
            <input className="input" inputMode="numeric" value={retention} onChange={(e) => (setRetention(e.target.value), setCheck(null))} />
          </label>
          <label className="field">
            <span>{t('backup.rate_limit')}</span>
            <input className="input" inputMode="decimal" placeholder={t('backup.unlimited')} value={rateMbps} onChange={(e) => setRateMbps(e.target.value)} />
          </label>
          <label className="field">
            <span>{t('backup.policy_quota')}</span>
            <input className="input" inputMode="decimal" placeholder={t('backup.auto')} value={quotaGB} onChange={(e) => setQuotaGB(e.target.value)} />
          </label>
        </div>
        <FaultFields value={fd} onChange={(v) => (setFd(v), setCheck(null))} prefix="source" />
        <label className="check">
          <input type="checkbox" checked={confirmed} onChange={(e) => setConfirmed(e.target.checked)} />
          {t('backup.site_confirmed')}
        </label>
        {tgt?.kind === 'nvr' && !faultComplete(fd) && <p className="muted small">{t('backup.fd_required_nvr')}</p>}
        {check && <CheckView check={check} />}
        {err && <p className="error">{err}</p>}
        <div className="row">
          <button className="btn" onClick={() => void run(true)} disabled={busy || !ready} data-testid="backup-estimate">
            {t('backup.estimate')}
          </button>
          <button className="btn btn-primary" onClick={() => void run(false)} disabled={busy || !ready || !check} data-testid="backup-submit">
            {t('backup.submit')}
          </button>
        </div>
      </div>
    </Dialog>
  );
}

function CheckView({ check }: { check: BackupCheck }) {
  const { t } = useTranslation();
  const e = check.estimate;
  return (
    <div className="card backup-check" data-testid="backup-check">
      {e && (
        <>
          <div>
            {t('backup.est_line', {
              bps: fmtBps(e.total_bps),
              days: e.retention_days,
              raw: fmtBytes(e.raw_bytes),
              total: fmtBytes(e.required_bytes),
              oh: Math.round(e.overhead_ratio * 100),
              margin: Math.round(e.margin_ratio * 100),
            })}
          </div>
          {e.assumed && e.assumed.length > 0 && <div className="muted small">{t('backup.est_assumed', { n: e.assumed.length })}</div>}
          <div className="muted small">{t('backup.est_note')}</div>
        </>
      )}
      {check.target_free_bytes !== undefined && <div className="muted small">{t('backup.target_free', { free: fmtBytes(check.target_free_bytes) })}</div>}
      {check.offsite === false && (
        <div>
          <span className="badge badge-warn">{t('backup.not_offsite')}</span>
        </div>
      )}
      {check.fault_check?.reason && <div className="small">{check.fault_check.reason}</div>}
    </div>
  );
}

function PolicyCard({
  view: v,
  home,
  targets,
  onError,
  onChanged,
}: {
  view: BackupPolicyView;
  home: ScopeHome;
  targets: BackupTarget[];
  onError(e: string | null): void;
  onChanged(): void;
}) {
  const { t, i18n } = useTranslation();
  const { ask, dialog } = useConfirm();
  const { tree } = useDevices();
  const [busy, setBusy] = useState(false);
  const [restoring, setRestoring] = useState(false);
  const p = v.policy;
  const source = v.my_sides.includes('source');
  const api = home.endpoints.api;
  const nvr = tree.find((d) => d.aid === p.source_nvr_ref);
  const chName = (ref: string) => {
    for (const d of tree) for (const c of d.children) if (c.aid === ref) return fullName(c, tree);
    return ref;
  };
  const target = targets.find((x) => x.target_id === p.target_id);
  const sum = coverageSummary(v.coverage);

  async function act(fn: () => Promise<unknown>, confirm?: ConfirmOptions) {
    if (confirm && !(await ask(confirm))) return;
    setBusy(true);
    onError(null);
    try {
      await fn();
      onChanged();
    } catch (e) {
      onError(errText(t, e));
    } finally {
      setBusy(false);
    }
  }
  const fmt = (s?: string | null) => (s ? formatDateTime(Date.parse(s) / 1000, tz(), i18n.language) : '—');
  return (
    <li className="card backup-row" data-policy={p.policy_id} data-status={p.status}>
      <div className="backup-main">
        <div>
          <strong>
            {source ? (nvr ? fullName(nvr, tree) : p.source_nvr_ref) : t('backup.incoming')} → {v.target.name ?? target?.name ?? p.target_id}
          </strong>{' '}
          <span className={statusBadge(p.status)}>{t(`backup.pst_${p.status}`)}</span>{' '}
          {source && v.health && p.activated_at && <span className={statusBadge(v.health === 'ok' ? 'ok' : v.health)}>{t(`backup.cov_${v.health}`)}</span>}{' '}
          {p.offsite === false && <span className="badge badge-warn">{t('backup.not_offsite')}</span>}
        </div>
        {p.status_reason && <div className="muted small">{p.status_reason}</div>}
        <div className="muted small">
          {t('backup.policy_line', {
            ch: source ? (p.sources?.length ?? 0) : (p.channels ?? 0),
            days: p.retention_days,
            quota: fmtBytes(p.quota_bytes),
            rate: p.rate_limit_bps ? fmtBps(p.rate_limit_bps) : t('backup.unlimited'),
            v: p.policy_version,
          })}
        </div>
        {v.replica_states && (v.replica_states.deleting || v.replica_states.deletion_pending || v.replica_states.deleted || v.replica_states.corrupt || v.replica_states.lost) ? (
          <div className="small" data-testid="backup-states">
            {(['deleting', 'deletion_pending', 'deleted', 'corrupt', 'lost'] as const)
              .filter((k) => v.replica_states?.[k])
              .map((k) => t('backup.rs_' + k, { n: v.replica_states?.[k] }))
              .join(t('common.list_sep'))}
          </div>
        ) : null}
        {source && <RestoreJobs policy={p} home={home} onError={onError} />}
        {!source && (
          <div className="muted small" data-testid="backup-target-view">
            {t('backup.target_view', { n: v.object_count ?? 0, bytes: fmtBytes(v.stored_bytes ?? 0) })}
          </div>
        )}
        {source && v.coverage && p.activated_at && (
          <>
            <div className="small">
              {t('backup.cov_line', { until: fmt(sum.oldestProtected), lag: fmtLag(sum.maxLag), pending: fmtBytes(sum.pendingBytes), missing: sum.missing })}
            </div>
            <table className="table small backup-cov">
              <thead>
                <tr>
                  <th>{t('backup.col_channel')}</th>
                  <th>{t('backup.col_state')}</th>
                  <th>{t('backup.col_protected')}</th>
                  <th>{t('backup.col_lag')}</th>
                  <th>{t('backup.col_pending')}</th>
                  <th>{t('backup.col_missing')}</th>
                  <th>{t('backup.col_verified')}</th>
                </tr>
              </thead>
              <tbody>
                {v.coverage.map((c) => (
                  <tr key={c.channel_ref} data-channel={c.channel_ref} data-state={c.state}>
                    <td>{chName(c.channel_ref)}</td>
                    <td>{t(`backup.cov_${c.state}`)}</td>
                    <td>{fmt(c.protected_until)}</td>
                    <td>{fmtLag(c.lag_seconds)}</td>
                    <td>
                      {fmtBytes(c.pending_bytes)} ({c.pending_count})
                    </td>
                    <td title={c.missing_ranges.map((m) => `${fmt(m.from)}–${fmt(m.to)} ${m.reason}`).join('\n')}>{c.missing_ranges.length}</td>
                    <td>{fmt(c.last_verified_at)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </>
        )}
      </div>
      <div className="export-actions">
        {v.can_approve.map((side) => (
          <button
            key={side}
            className="btn btn-sm btn-primary"
            disabled={busy}
            data-testid={`backup-approve-${side}`}
            onClick={() => void act(() => backupApi.approve(api, home.home_id, p.policy_id, { expected_policy_version: p.policy_version, side }))}
          >
            {t(`backup.approve_${side}`)}
          </button>
        ))}
        {(p.status === 'active' || p.status === 'degraded' || p.status === 'validating' || p.status === 'pending_approval') && (
          <button
            className="btn btn-sm"
            disabled={busy}
            onClick={() => void act(() => backupApi.patchPolicy(api, home.home_id, p.policy_id, { expected_version: p.version, action: 'pause' }))}
          >
            {t('backup.pause')}
          </button>
        )}
        {p.status === 'paused' && (
          <button
            className="btn btn-sm"
            disabled={busy}
            onClick={() => void act(() => backupApi.patchPolicy(api, home.home_id, p.policy_id, { expected_version: p.version, action: 'resume' }))}
          >
            {t('backup.resume')}
          </button>
        )}
        {source && (p.status === 'active' || p.status === 'degraded' || p.status === 'paused' || p.status === 'revoked') && (
          <button className="btn btn-sm" disabled={busy} onClick={() => setRestoring(true)} data-testid="backup-restore">
            {t('backup.restore')}
          </button>
        )}
        {source && (
          <button
            className="btn btn-sm"
            disabled={busy}
            data-testid="backup-purge"
            onClick={() =>
              void act(() => backupApi.purge(api, home.home_id, p.policy_id, p.version), {
                title: t('backup.confirm_purge'),
                action: t('common.action_purge'),
                typeToConfirm: target?.name ?? p.policy_id,
              })
            }
          >
            {t('backup.purge')}
          </button>
        )}
        {p.status !== 'revoked' && (
          <button
            className="btn btn-sm"
            disabled={busy}
            onClick={() =>
              void act(() => backupApi.patchPolicy(api, home.home_id, p.policy_id, { expected_version: p.version, action: 'revoke' }), {
                title: t('backup.confirm_revoke'),
                action: t('common.action_revoke'),
                danger: true,
              })
            }
          >
            {t('backup.revoke')}
          </button>
        )}
      </div>
      {restoring && <RestoreDialog policy={p} home={home} close={() => setRestoring(false)} done={() => (setRestoring(false), onChanged())} />}
      {dialog}
    </li>
  );
}

function RestoreJobs({ policy, home, onError }: { policy: BackupPolicy; home: ScopeHome; onError(e: string | null): void }) {
  const { t } = useTranslation();
  const q = useQueries({
    queries: [{ queryKey: ['backup-restore', policy.policy_id], queryFn: () => backupApi.restoreJobs(home.endpoints.api, home.home_id, policy.policy_id), refetchInterval: 5000 }],
  })[0];
  const jobs = (q.data ?? []).slice(0, 5);
  if (jobs.length === 0) return null;
  return (
    <ul className="backup-restore small" data-testid="backup-restore-jobs">
      {jobs.map((j) => (
        <li key={j.job_id} data-job={j.job_id} data-status={j.status}>
          {t('backup.restore_line', { status: t('backup.rst_' + j.status), done: j.restored.length, total: j.total, failed: j.failures.length, nvr: j.target_nvr_ref })}
          {(j.status === 'queued' || j.status === 'running') && (
            <button
              className="btn btn-sm"
              style={{ marginLeft: 8 }}
              onClick={() => void backupApi.cancelRestore(home.endpoints.api, home.home_id, policy.policy_id, j.job_id).then(() => q.refetch(), (e) => onError(errText(t, e)))}
            >
              {t('common.cancel')}
            </button>
          )}
        </li>
      ))}
    </ul>
  );
}

function RestoreDialog({ policy, home, close, done }: { policy: BackupPolicy; home: ScopeHome; close(): void; done(): void }) {
  const { t } = useTranslation();
  const { tree } = useDevices();
  const nvrs = tree.filter((d) => d.homeId === home.home_id && d.kind !== 'child' && d.children.length > 0);
  const [target, setTarget] = useState(policy.source_nvr_ref ?? nvrs[0]?.aid ?? '');
  const chs = policy.sources?.map((c) => c.channel_ref) ?? [];
  const [picked, setPicked] = useState<string[]>(chs);
  const now = Date.now();
  const local = (ms: number) => {
    const d = new Date(ms);
    const z = (n: number) => String(n).padStart(2, '0');
    return `${d.getFullYear()}-${z(d.getMonth() + 1)}-${z(d.getDate())}T${z(d.getHours())}:${z(d.getMinutes())}`;
  };
  const [from, setFrom] = useState(local(now - 3600_000));
  const [to, setTo] = useState(local(now));
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const chName = (ref: string) => {
    for (const d of tree) for (const c of d.children) if (c.aid === ref) return fullName(c, tree);
    return ref;
  };
  async function submit() {
    setBusy(true);
    setErr(null);
    try {
      await backupApi.createRestore(home.endpoints.api, home.home_id, policy.policy_id, {
        target_nvr_ref: target,
        channels: picked.length === chs.length ? undefined : picked,
        from: new Date(from).toISOString(),
        to: new Date(to).toISOString(),
      });
      done();
    } catch (e) {
      setErr(errText(t, e));
    } finally {
      setBusy(false);
    }
  }
  return (
    <Dialog open title={t('backup.restore')} onClose={close}>
      <div className="form" data-testid="backup-restore-form">
        <p className="muted small">{t('backup.restore_help')}</p>
        <label className="field">
          <span>{t('backup.restore_target')}</span>
          <select className="input" value={target} onChange={(e) => setTarget(e.target.value)}>
            {nvrs.map((d) => (
              <option key={d.aid} value={d.aid}>
                {fullName(d, tree)}
              </option>
            ))}
          </select>
        </label>
        <fieldset className="field">
          <legend>{t('backup.channels')}</legend>
          <div className="check-grid">
            {chs.map((c) => (
              <label key={c} className="check">
                <input type="checkbox" checked={picked.includes(c)} onChange={(e) => setPicked(e.target.checked ? [...picked, c] : picked.filter((x) => x !== c))} />
                {chName(c)}
              </label>
            ))}
          </div>
        </fieldset>
        <div className="check-grid">
          <label className="field">
            <span>{t('backup.from')}</span>
            <DateTimeInput value={from} onChange={setFrom} />
          </label>
          <label className="field">
            <span>{t('backup.to')}</span>
            <DateTimeInput value={to} onChange={setTo} />
          </label>
        </div>
        {err && <p className="error">{err}</p>}
        <div className="row">
          <button className="btn btn-primary" onClick={() => void submit()} disabled={busy || !target || picked.length === 0} data-testid="backup-restore-submit">
            {t('backup.restore_start')}
          </button>
        </div>
      </div>
    </Dialog>
  );
}
